/* File: frontend/src/App.js */
/* Version: 2.1 (2025-12-17) */
/* Description: Connected real ClientProfilePage and updated route params. */

import React, { Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider, useBranding } from './context/BrandingContext'; // [Phase 07.1] Tenant identity + theme gating
import { ReputationTasksProvider } from './context/ReputationTasksContext'; // [NEW]
import { UiLanguageProvider } from './context/UiLanguageContext'; // [Phase 9] язык интерфейса по признаку арендатора
import { mlgTheme, fitenkoTheme } from './theme'; // [Phase 07.1] MUI themes per tenant

// --- 2. ИМПОРТ КОМПОНЕНТОВ И СТРАНИЦ ---
import Navbar from './components/Navbar';
import SectionSubnav from './components/SectionSubnav'; // [quick-260908-pun] полоса подраздела fitenko
import LoadingSpinner from './components/LoadingSpinner';
import PublicChatWidget from './components/PublicChatWidget';
import KatifiWidget from './components/KatifiWidget'; // [NEW] Phase 11
import PublicCalendarWidget from './components/PublicCalendarWidget'; // [NEW]
import InboxWorkspace from './components/InboxWorkspace'; // [NEW] Sprint 1 Omnichannel Inbox
import AIOperationsHub from './components/AIOperationsHub'; // [NEW] Sprint 3 AI Operations Hub
import WidgetLayout from './layouts/WidgetLayout';
import WidgetPage from './pages/WidgetPage';
import CommandPalette from './components/CommandPalette';

// Основные страницы
import Home from './pages/Home';
import Login from './pages/Login';
import DashboardPage from './pages/Dashboard';
import AIParalegal from './pages/AIParalegal';
import DocumentsPage from './pages/DocumentsPage';
import ReputationDashboard from './pages/ReputationDashboard';
import UserManagement from './components/UserManagement'; // Обратите внимание: это компонент, но используется как страница
import IntakeFormPage from './pages/IntakeFormPage';
import NewCasePage from './pages/NewCasePage';
import CaseDetailPage from './pages/CaseDetailPage';
import CasesPage from './pages/CasesPage';
import LeadHistoryPage from './pages/LeadHistoryPage';
import HistoryPage from './pages/HistoryPage'; // [quick-260909-v7b] экран «История» арендатора fitenko
import LeadsPage from './pages/LeadsPage';
import EmailTriagePage from './pages/EmailTriagePage'; // [NEW] Email Inbox (Email Triage)
import CalendarPage from './pages/CalendarPage';
import UserProfileAdmin from './pages/UserProfileAdmin';

// === [NEW] Импорт новой страницы профиля клиента ===
import ClientProfilePage from './pages/ClientProfilePage';
import AdminBrainCenter from './pages/AdminBrainCenter'; // [NEW] Brain Center
import VoicePage from './pages/VoicePage'; // [NEW] AI Voice Paralegal
import LiveMonitorPage from './pages/LiveMonitorPage'; // [NEW] Live Monitor
import TasksPage from './pages/TasksPage'; // [Protocol v8.0] AI Task Center
import OrganicSEOPage from './pages/organic-seo/OrganicSEOPage'; // [NEW] Organic SEO
import AiStaffPage from './pages/AiStaffPage'; // [NEW] AI Staff Dashboard
import MagicExecutorPage from './pages/MagicExecutorPage'; // [NEW] Magic URL Executor
import AuditLogPage from './pages/AuditLogPage'; // [NEW] Audit Log Dashboard
import SeoAnalyticsPage from './pages/SeoAnalyticsPage'; // [NEW] SEO Analytics Dashboard
import ClientPortalPage from './pages/ClientPortalPage'; // [Phase 05] Client Portal
import DocumentGeneratorPage from './pages/DocumentGeneratorPage'; // [Phase 05] Document Generator
import AdminInstanceProfile from './pages/AdminInstanceProfile'; // [Phase 06.1] Creator-only instance profile editor
// ==================================================
// ==================================================

// --- 3. КОМПОНЕНТЫ-ЗАГЛУШКИ ---
const CaseAnalysisPage = () => <div className="container card"><h2>Case Analysis Page (Placeholder)</h2><p>This page will show the detailed AI analysis of a case.</p></div>;
// TasksPage imported from ./pages/TasksPage.js
const TaskDetailPage = () => <div className="container card"><h2>Task Detail Page (Placeholder)</h2><p>This page will show the details of a specific task.</p></div>;

// (Заглушка ClientProfilePage удалена, теперь используется реальный файл)

// --- 4. КОМПОНЕНТ ЗАЩИЩЕННОГО МАКЕТА ---
const ProtectedLayout = () => {
    const { user } = useAuth();
    const { isFitenko } = useBranding();
    return (
        <div className="App">
            <Navbar />
            {isFitenko && <SectionSubnav />}   {/* ДОСТИЖИМОСТЬ СВЁРНУТОГО */}
            <CommandPalette />
            <main className="main-content">
                <Outlet />
            </main>
        </div>
    );
};

// --- 5. КОМПОНЕНТ С МАРШРУТАМИ ---
const AppRoutes = () => {
    const { user, loading } = useAuth();

    if (loading) {
        return <LoadingSpinner message="Verifying authentication..." />;
    }

    return (
        <Routes>
            {/* Публичные маршруты */}
            <Route path="/" element={<Home />} />
            <Route
                path="/login"
                element={user ? <Navigate to="/dashboard" /> : <Login />}
            />

            <Route path="/widget" element={
                <WidgetLayout>
                    <WidgetPage />
                </WidgetLayout>
            } />

            {/* Защищенные маршруты */}
            <Route
                element={user ? <ProtectedLayout /> : <Navigate to="/login" />}
            >
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/ai-paralegal" element={<AIParalegal />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route path="/reputation" element={<ReputationDashboard />} />

                <Route path="/user-management" element={<UserManagement />} />
                <Route path="/users" element={<Navigate to="/user-management" replace />} />

                <Route path="/leads" element={<LeadsPage />} />
                <Route path="/leads/:id" element={<LeadHistoryPage />} />
                {/* [quick-260909-v7b] РОВНО ОДИН новый маршрут. Выбранный человек
                    живёт в строке адреса как ?lead=<id>, а не отдельным путём:
                    экран остаётся один и делится ссылкой. */}
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/inbox" element={<InboxWorkspace />} /> {/* [NEW] Sprint 1 Omnichannel Inbox */}
                <Route path="/ai-ops" element={<AIOperationsHub />} /> {/* [NEW] Sprint 3 AI Operations Hub */}
                <Route path="/email-inbox" element={<EmailTriagePage />} /> {/* [NEW] Email Inbox (Email Triage) */}
                <Route path="/ai-staff" element={<AiStaffPage />} />
                <Route path="/magic-executor" element={<MagicExecutorPage />} />
                <Route path="/cases" element={<CasesPage />} />
                <Route path="/cases/new" element={<NewCasePage />} />
                <Route path="/cases/:caseId" element={<CaseDetailPage />} />
                <Route path="/cases/:caseId/intake" element={<IntakeFormPage />} />
                <Route path="/cases/:caseId/analysis" element={<CaseAnalysisPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/tasks/:taskId" element={<TaskDetailPage />} />

                {/* === [UPDATED] Маршрут к профилю клиента === */}
                {/* Используем :id, чтобы совпадало с useParams в ClientProfilePage.js */}
                <Route path="/clients/:id" element={<ClientProfilePage />} />
                {/* =========================================== */}

                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/profile" element={<UserProfileAdmin />} />
                <Route path="/admin/brain" element={<AdminBrainCenter />} /> {/* [NEW] Brain Center Route */}
                <Route path="/voice" element={<VoicePage />} /> {/* [NEW] AI Voice Paralegal Route */}
                <Route path="/monitor" element={<LiveMonitorPage />} /> {/* [NEW] Live Monitor Route */}
                <Route path="/organic-seo" element={<OrganicSEOPage />} /> {/* [NEW] Organic SEO Route */}
                <Route path="/seo-analytics" element={<SeoAnalyticsPage />} /> {/* [NEW] SEO Analytics Dashboard */}
                <Route path="/admin/audit-log" element={<AuditLogPage />} /> {/* [NEW] Audit Log Route */}
                <Route path="/client-portal" element={<ClientPortalPage />} /> {/* [Phase 05] Client Portal */}
                <Route path="/document-generator" element={<DocumentGeneratorPage />} /> {/* [Phase 05] Document Generator */}
                <Route path="/admin/company-profile" element={<AdminInstanceProfile />} /> {/* [Phase 06.1] Creator-only instance profile editor */}

            </Route>

            {/* [NEW] Embed Route for Calendar */}
            <Route path="/embed/calendar" element={<PublicCalendarWidget />} />



            {/* "Catch-all" маршрут */}
            <Route path="*" element={<Navigate to={user ? "/dashboard" : "/"} />} />
        </Routes>
    );
};

// --- 6. ERROR BOUNDARY ---
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        console.error('App ErrorBoundary:', error, errorInfo);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>
                    <h2 style={{ color: '#dc2626' }}>Something went wrong</h2>
                    <p style={{ color: '#6b7280', marginTop: '8px' }}>{this.state.error?.message}</p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{ marginTop: '16px', padding: '8px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    >
                        Reload Page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

// --- 7. ГЛАВНЫЙ КОМПОНЕНТ ---
function App() {
    const AppWrapper = () => {
        const { isAuthenticated } = useAuth();
        const location = useLocation();
        const isEmbed = location.pathname.startsWith('/embed');
        const isLogin = location.pathname.startsWith('/login');

        return (
            <>
                <AppRoutes />
                {/* Global Katifi Chat Widget */}
                {isAuthenticated && !isEmbed && <KatifiWidget />}

                {/* Hide chat widget if user is logged in, on an embed page, or on the login page */}
                {!isAuthenticated && !isEmbed && !isLogin && <PublicChatWidget />}
            </>
        )
    }

    // [Phase 07.1] AppShell reads useBranding() to pick MUI theme per tenant.
    // Must be a CHILD of BrandingProvider — useBranding() returns the default before
    // the provider mounts otherwise.
    const AppShell = () => {
        const { isFitenko } = useBranding();
        const theme = isFitenko ? fitenkoTheme : mlgTheme;
        return (
            <ThemeProvider theme={theme}>
                <Router>
                    <AuthProvider>
                        <ReputationTasksProvider>
                            <Toaster position="bottom-right" />
                            <AppWrapper />
                        </ReputationTasksProvider>
                    </AuthProvider>
                </Router>
            </ThemeProvider>
        );
    };

    return (
        <ErrorBoundary>
            {/* StyledEngineProvider injectFirst — MUST be injectFirst so Tailwind utilities
                (specificity 1) can override MUI defaults without !important.
                See RESEARCH §"Pitfall 1". */}
            <StyledEngineProvider injectFirst>
                <BrandingProvider>
                    <UiLanguageProvider>
                        <AppShell />
                    </UiLanguageProvider>
                </BrandingProvider>
            </StyledEngineProvider>
        </ErrorBoundary>
    );
}

export default App;